import { Box, Container, Typography, Button, IconButton, Tooltip } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import MapIcon from '@mui/icons-material/Map';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { resolveLogoSrc } from '../../utils/logoSrc';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import CartIconButton from '../cart/CartIconButton';
import AuthButtonsSkeleton from './AuthButtonsSkeleton';
import LocationPickerModal from './LocationPickerModal';

const heroAuthButtonSx = {
  borderColor: '#ffffff',
  color: '#ffffff',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  px: { xs: 1.5, sm: 2, md: 3 },
  py: { xs: 0.5, md: 1 },
  fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '1rem' },
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '8px',
  borderWidth: '2px',
  minWidth: 0,
  flexShrink: 1,
  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  '&:hover': {
    borderWidth: '2px',
    borderColor: '#ffffff',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
  },
  transition: 'all 0.2s ease',
} as const;

const heroIconButtonSx = {
  color: '#ffffff',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
} as const;

const heroAuthControlsSx = {
  display: 'flex',
  alignItems: 'center',
  gap: { xs: 1, sm: 2 },
  zIndex: 10,
  flexWrap: 'wrap',
  width: { xs: '100%', sm: 'auto' },
  maxWidth: { xs: '100%', sm: 'calc(100% - 48px)' },
  justifyContent: { xs: 'center', sm: 'flex-end' },
  mb: { xs: 2, sm: 0 },
  position: { xs: 'relative', sm: 'absolute' },
  top: { sm: 16, md: 24 },
  right: { sm: 24, md: 48 },
} as const;

type HeroAuthControlsProps = {
  isAuthStateLoading: boolean;
  user: ReturnType<typeof useUser>['user'];
  handleLoginClick: () => void;
  logoutUser: () => void;
  onOpenLocationPicker: () => void;
};

const HeroAuthControls = ({
  isAuthStateLoading,
  user,
  handleLoginClick,
  logoutUser,
  onOpenLocationPicker,
}: HeroAuthControlsProps) => (
  <Box sx={heroAuthControlsSx}>
    {isAuthStateLoading && <AuthButtonsSkeleton variant="light" />}
    {!isAuthStateLoading && !user && (
      <>
        <Button component={Link} to="/signup" variant="outlined" sx={heroAuthButtonSx}>
          Sign Up
        </Button>
        <Button onClick={handleLoginClick} variant="outlined" sx={heroAuthButtonSx}>
          Login
        </Button>
      </>
    )}
    {!isAuthStateLoading && user && (
      <>
        <Button component={Link} to="/profile" variant="outlined" sx={heroAuthButtonSx}>
          Family Profiles
        </Button>
        <Tooltip title="Choose Location" arrow>
          <IconButton onClick={onOpenLocationPicker} sx={heroIconButtonSx}>
            <MapIcon />
          </IconButton>
        </Tooltip>
        <CartIconButton variant="light" />
        <Tooltip title="Logout" arrow>
          <IconButton onClick={logoutUser} sx={heroIconButtonSx}>
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </>
    )}
  </Box>
);

const HeroSection = () => {
  const { companyName, heroTagline, logoUrl } = usePublicSettings();
  const heroLogoSrc = resolveLogoSrc(logoUrl, import.meta.env.VITE_LOGO_BUCKET_URL ?? '');
  const taglineText = heroTagline?.trim();
  const { handleLoginClick, logoutUser, isUserDataLoading } = useAuth();
  const { user, isUserLoaded } = useUser();
  const isAuthStateLoading = !isUserLoaded || isUserDataLoading;
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);

  return (
    <Box
      sx={{
        background: 'transparent',
        minHeight: { xs: '280px', md: '280px' },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pt: { xs: 2, md: 5 },
        pb: { xs: 4, md: 5 },
        px: 2,
        position: 'relative',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <HeroAuthControls
        isAuthStateLoading={isAuthStateLoading}
        user={user}
        handleLoginClick={handleLoginClick}
        logoutUser={logoutUser}
        onOpenLocationPicker={() => setIsLocationPickerOpen(true)}
      />

      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 2, md: 3 },
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          {heroLogoSrc && (
            <Box
              component="img"
              src={heroLogoSrc}
              alt="Company Logo"
              sx={{
                width: { xs: 'min(187.5px, 80vw)', sm: '225px', md: '262.5px' },
                maxWidth: '100%',
                height: 'auto',
                objectFit: 'contain',
                filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))',
                flexShrink: 0,
              }}
            />
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              alignItems: 'center',
            }}
          >
            {companyName && (
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '2.5rem', md: '3.125rem' },
                  color: '#ffffff',
                  textAlign: 'center',
                  letterSpacing: '-0.02em',
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  maxWidth: '100%',
                  wordBreak: 'break-word',
                }}
              >
                {companyName}
              </Typography>
            )}

            {taglineText && (
              <Typography
                variant="h6"
                component="p"
                sx={{
                  fontWeight: 400,
                  fontSize: {
                    xs: '1.125rem',
                    sm: '1.25rem',
                    md: '1.40625rem',
                  },
                  color: '#ffffff',
                  textAlign: 'center',
                  lineHeight: 1.5,
                  textShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  maxWidth: '100%',
                  wordBreak: 'break-word',
                }}
              >
                {taglineText}
              </Typography>
            )}
          </Box>
        </Box>
      </Container>
      <LocationPickerModal open={isLocationPickerOpen} onClose={() => setIsLocationPickerOpen(false)} />
    </Box>
  );
};

export default HeroSection;
